<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import { Plus, RotateCcw, Search, FileDown } from '@lucide/vue'
import {
  Button,
  EmptyState,
  Input,
  PageHeader,
  Pagination,
  Section,
  Select,
  Spinner,
  Stat,
  Table,
  Toolbar,
  useToast,
} from '@drama/ui'
import { useI18n } from 'vue-i18n'
import WaitlistActionModal from '../components/WaitlistActionModal.vue'
import WaitlistStatusBadge from '../components/WaitlistStatusBadge.vue'
import { waitlistRowActions } from '../components/waitlist-actions.js'
import {
  addWaitlistNote,
  createManualWaitlistEntry,
  exportWaitlistEntries,
  inviteWaitlistEntry,
  listWaitlistEntries,
  rejectWaitlistEntry,
  resendWaitlistInvite,
} from '../api/waitlist.js'

const STATUSES = ['joined', 'invite_pending', 'invited', 'registered', 'rejected']
const SOURCES = ['landing_hero', 'landing_footer', 'pricing', 'manual_admin']

const { t } = useI18n()
const router = useRouter()
const toast = useToast()

const filters = reactive({
  q: '',
  status: '',
  source: '',
  use_case: '',
})

const entries = ref([])
const metrics = reactive({
  total: 0,
  joined: 0,
  invite_pending: 0,
  invited: 0,
  registered: 0,
  rejected: 0,
  failed_deliveries: 0,
})
const loading = ref(false)
const error = ref('')
const nextCursor = ref('')
const cursorStack = ref([])
const currentCursor = ref('')
const modal = reactive({
  open: false,
  type: 'manual',
  loading: false,
  error: '',
  entry: null,
})

const columns = computed(() => [
  { key: 'email', label: t('visualGameAdmin.waitlist.table.email'), width: '220px' },
  { key: 'display_name', label: t('visualGameAdmin.waitlist.table.displayName'), width: '140px' },
  { key: 'status', label: t('visualGameAdmin.waitlist.table.status'), width: '110px' },
  { key: 'invite_state', label: t('visualGameAdmin.waitlist.table.inviteState'), width: '110px' },
  { key: 'source', label: t('visualGameAdmin.waitlist.table.source'), width: '110px' },
  { key: 'use_case', label: t('visualGameAdmin.waitlist.table.useCase'), width: '160px' },
  { key: 'created_at', label: t('visualGameAdmin.waitlist.table.createdAt'), width: '150px', mono: true },
  { key: 'updated_at', label: t('visualGameAdmin.waitlist.table.updatedAt'), width: '150px', mono: true },
  { key: 'last_event', label: t('visualGameAdmin.waitlist.table.lastEvent'), width: '160px' },
  { key: 'actions', label: t('visualGameAdmin.common.actions'), width: '120px' },
])

const statusOptions = computed(() => [
  { value: '', label: t('visualGameAdmin.waitlist.filters.all') },
  ...STATUSES.map(value => ({ value, label: t(`visualGameAdmin.waitlist.status.${value}`) })),
])
const sourceOptions = computed(() => [
  { value: '', label: t('visualGameAdmin.waitlist.filters.all') },
  ...SOURCES.map(value => ({ value, label: t(`visualGameAdmin.waitlist.source.${value}`) })),
])

const activeQuery = computed(() => buildQuery(currentCursor.value))

onMounted(() => {
  loadEntries('')
})

async function loadEntries(cursor) {
  loading.value = true
  error.value = ''
  currentCursor.value = cursor
  try {
    const response = await listWaitlistEntries(activeQuery.value)
    entries.value = response.items
    updatePageMetrics(response.items)
    nextCursor.value = response.next_cursor
  } catch (err) {
    error.value = err.message
  } finally {
    loading.value = false
  }
}

function buildQuery(cursor) {
  const query = { limit: 50 }
  if (filters.q) query.q = filters.q
  if (filters.status) query.status = filters.status
  if (filters.source) query.source = filters.source
  if (filters.use_case) query.use_case = filters.use_case
  if (cursor) query.cursor = cursor
  return query
}

function applyFilters() {
  cursorStack.value = []
  loadEntries('')
}

function resetFilters() {
  filters.q = ''
  filters.status = ''
  filters.source = ''
  filters.use_case = ''
  cursorStack.value = []
  loadEntries('')
}

function nextPage() {
  cursorStack.value = [...cursorStack.value, currentCursor.value]
  loadEntries(nextCursor.value)
}

function previousPage() {
  const stack = [...cursorStack.value]
  const previousCursor = stack.pop()
  cursorStack.value = stack
  loadEntries(previousCursor)
}

function openModal(type, entry = null) {
  modal.open = true
  modal.type = type
  modal.entry = entry
  modal.error = ''
}

async function submitModal(input) {
  modal.loading = true
  modal.error = ''
  try {
    if (modal.type === 'manual') {
      const created = await createManualWaitlistEntry(input)
      modal.open = false
      router.push({ name: 'waitlist-detail', params: { id: created.id } })
      return
    }
    if (modal.type === 'export') {
      const csv = await exportWaitlistEntries(input)
      downloadCsv(csv)
      modal.open = false
      toast.success(t('visualGameAdmin.waitlist.toast.exported'))
      return
    }
    const entryId = modal.entry.id
    if (modal.type === 'invite') await inviteWaitlistEntry(entryId, input)
    if (modal.type === 'resend') await resendWaitlistInvite(entryId, input)
    if (modal.type === 'note') await addWaitlistNote(entryId, input)
    if (modal.type === 'reject') await rejectWaitlistEntry(entryId, input)
    modal.open = false
    toast.success(t('visualGameAdmin.waitlist.toast.saved'))
    await loadEntries(currentCursor.value)
  } catch (err) {
    modal.error = err.message
    toast.error(t('visualGameAdmin.waitlist.toast.failed'), { duration: 0 })
  } finally {
    modal.loading = false
  }
}

function downloadCsv(csv) {
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }))
  const link = document.createElement('a')
  link.href = url
  link.download = `visual-game-waitlist-${Date.now()}.csv`
  link.click()
  URL.revokeObjectURL(url)
}

function runRowAction(action, entry) {
  switch (action) {
    case 'view':
      router.push({ name: 'waitlist-detail', params: { id: entry.id } })
      return
    case 'invite':
    case 'resend':
    case 'note':
    case 'reject':
      openModal(action, entry)
      return
    default:
      throw new Error(`Unhandled row action: ${action}, id: ${entry.id}`)
  }
}

function metricItems() {
  return [
    ['total', t('visualGameAdmin.waitlist.metrics.total'), metrics.total],
    ['joined', t('visualGameAdmin.waitlist.metrics.joined'), metrics.joined],
    ['invite_pending', t('visualGameAdmin.waitlist.metrics.invitePending'), metrics.invite_pending],
    ['invited', t('visualGameAdmin.waitlist.metrics.invited'), metrics.invited],
    ['registered', t('visualGameAdmin.waitlist.metrics.registered'), metrics.registered],
    ['rejected', t('visualGameAdmin.waitlist.metrics.rejected'), metrics.rejected],
    ['failed_deliveries', t('visualGameAdmin.waitlist.metrics.failedDeliveries'), metrics.failed_deliveries],
  ]
}

function updatePageMetrics(items) {
  metrics.total = items.length
  metrics.joined = countStatus(items, 'joined')
  metrics.invite_pending = countStatus(items, 'invite_pending')
  metrics.invited = countStatus(items, 'invited')
  metrics.registered = countStatus(items, 'registered')
  metrics.rejected = countStatus(items, 'rejected')
  metrics.failed_deliveries = countFailedDeliveries(items)
}

function countStatus(items, status) {
  let count = 0
  for (const item of items) {
    if (item.status === status) count += 1
  }
  return count
}

function countFailedDeliveries(items) {
  let count = 0
  for (const item of items) {
    if (item.last_invite_delivery_status === 'failed') count += 1
  }
  return count
}

function formatOptional(value) {
  return value === null || value === '' ? '-' : value
}
</script>

<template>
  <div class="waitlist-page vga-page">
    <PageHeader :title="t('visualGameAdmin.waitlist.title')">
      <template #actions>
        <Button size="sm" @click="openModal('manual')">
          <Plus :size="14" />
          {{ t('visualGameAdmin.common.create') }}
        </Button>
      </template>
    </PageHeader>

    <div class="waitlist-page__stats" data-insp-path="apps/visual-game-admin/src/pages/Waitlist.vue#metrics">
      <Stat v-for="[key, label, value] in metricItems()" :key="key" :label="label" :value="value" />
    </div>

    <Section data-insp-path="apps/visual-game-admin/src/pages/Waitlist.vue#filters">
      <Toolbar>
        <div class="drama-toolbar-field drama-toolbar-field--wide">
          <label>{{ t('visualGameAdmin.waitlist.filters.q') }}</label>
          <Input v-model="filters.q" type="search" size="sm" @keyup.enter="applyFilters" />
        </div>
        <div class="drama-toolbar-field">
          <label>{{ t('visualGameAdmin.waitlist.filters.status') }}</label>
          <Select v-model="filters.status" :options="statusOptions" size="sm" />
        </div>
        <div class="drama-toolbar-field">
          <label>{{ t('visualGameAdmin.waitlist.filters.source') }}</label>
          <Select v-model="filters.source" :options="sourceOptions" size="sm" />
        </div>
        <div class="drama-toolbar-field">
          <label>{{ t('visualGameAdmin.waitlist.filters.useCase') }}</label>
          <Input v-model="filters.use_case" size="sm" @keyup.enter="applyFilters" />
        </div>

        <template #actions>
          <Button size="sm" :loading="loading" @click="applyFilters">
            <Search :size="14" />
            {{ t('visualGameAdmin.waitlist.filters.search') }}
          </Button>
          <Button variant="ghost" size="sm" :disabled="loading" @click="resetFilters">
            <RotateCcw :size="14" />
            {{ t('visualGameAdmin.common.reset') }}
          </Button>
          <Button variant="ghost" size="sm" :disabled="loading" @click="openModal('export')">
            <FileDown :size="14" />
            {{ t('visualGameAdmin.common.export') }}
          </Button>
        </template>
      </Toolbar>
    </Section>

    <Section :padded="false" data-insp-path="apps/visual-game-admin/src/pages/Waitlist.vue#table">
      <div v-if="loading && entries.length === 0" class="vga-state">
        <Spinner />
        <span>{{ t('visualGameAdmin.common.loading') }}</span>
      </div>
      <div v-else-if="error" class="vga-state">
        <p class="vga-error">{{ error }}</p>
        <Button variant="secondary" @click="loadEntries(currentCursor)">
          {{ t('visualGameAdmin.common.retry') }}
        </Button>
      </div>
      <EmptyState
        v-else-if="entries.length === 0"
        :title="t('visualGameAdmin.common.empty')"
      >
        <template #action>
          <Button variant="secondary" @click="resetFilters">
            {{ t('visualGameAdmin.common.reset') }}
          </Button>
        </template>
      </EmptyState>
      <Table v-else :columns="columns" :rows="entries">
        <template #email="{ row }">
          <span class="cell-email" :title="row.email">{{ row.email }}</span>
        </template>
        <template #display_name="{ row }">
          {{ formatOptional(row.display_name) }}
        </template>
        <template #status="{ row }">
          <WaitlistStatusBadge kind="status" :value="row.status" :entry-id="row.id" />
        </template>
        <template #invite_state="{ row }">
          <WaitlistStatusBadge kind="invite" :value="row.invite_state" :entry-id="row.id" />
        </template>
        <template #source="{ row }">
          {{ t(`visualGameAdmin.waitlist.source.${row.source}`) }}
        </template>
        <template #last_event="{ row }">
          {{ formatOptional(row.last_event) }}
        </template>
        <template #actions="{ row }">
          <div class="row-actions">
            <Button
              v-for="action in waitlistRowActions(row)"
              :key="action"
              :variant="action === 'reject' ? 'danger' : 'ghost'"
              size="sm"
              @click="runRowAction(action, row)"
            >
              {{ t(`visualGameAdmin.common.${action}`) }}
            </Button>
          </div>
        </template>
      </Table>
    </Section>

    <Pagination
      :has-prev="cursorStack.length > 0"
      :has-next="Boolean(nextCursor)"
      :loading="loading"
      :prev-label="t('visualGameAdmin.waitlist.pagination.previous')"
      :next-label="t('visualGameAdmin.waitlist.pagination.next')"
      @prev="previousPage"
      @next="nextPage"
    />

    <WaitlistActionModal
      v-model:open="modal.open"
      :type="modal.type"
      :loading="modal.loading"
      :error="modal.error"
      :filters="activeQuery"
      @submit="submitModal"
    />
  </div>
</template>

<style scoped>
.waitlist-page__stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: var(--drama-space-md);
}

.cell-email {
  display: inline-block;
  max-width: 280px;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: bottom;
}

.row-actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--drama-space-sm);
}
</style>
